import { useTranslations } from "next-intl";
import { ofetch } from "ofetch";
import { usePostHog } from "posthog-js/react";
import { useCallback, useRef, useState } from "react";
import { addMessage } from "@/components/Messages";
import { getLocalStorageJobIds, setLocalStorageJobId } from "./landing-upload";
import type { LandingUploadAttempt } from "./landing-upload-attempt";
import {
  executeLandingUploadAttempt,
  type LandingOwnershipPersistence,
  type LandingUploadCapture,
  type LandingUploadLifecycleResult,
  type LandingUploadResumeState,
  prepareLandingUploadResumeState,
} from "./landing-upload-lifecycle";
import { Uploader } from "./multipart-upload";

type UploadStatus = "INITIAL" | "LOADING" | "ERROR" | "SUCCESS";

const isAmbiguousRequestError = (error: unknown) => {
  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return status === undefined || status >= 500;
};

export const useInsertTranscriptionJobLanding = ({
  setUploadMessage,
  setUploadAlertOpen,
}: {
  setUploadMessage: (message: string) => void;
  setUploadAlertOpen: (open: boolean) => void;
}) => {
  const t = useTranslations();
  const posthog = usePostHog();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("INITIAL");
  const [jobId, setJobId] = useState<string>();
  const [ownershipPersistence, setOwnershipPersistence] = useState<LandingOwnershipPersistence>();
  const activeAttemptRef = useRef<LandingUploadResumeState | null>(null);
  const uploader = useRef<Uploader | null>(null);

  const captureLandingEvent = useCallback<LandingUploadCapture>(
    (event, properties) => {
      posthog?.capture(event, properties);
    },
    [posthog]
  );

  const uploadFile = async (createdJobId: string, file: File) => {
    const uploaderInstance = new Uploader({
      file,
      fileName: `user_uploads/${createdJobId}`,
    });
    uploader.current = uploaderInstance;

    await new Promise<void>((resolve, reject) => {
      uploaderInstance.onComplete(() => resolve());
      uploaderInstance.onError(reject);
      void uploaderInstance.start();
    });
  };

  const resetActiveAttempt = () => {
    activeAttemptRef.current = null;
    setOwnershipPersistence(undefined);
  };

  const handleInsertTranscriptionJob = async ({
    attempt,
    hasCredits,
  }: {
    attempt: LandingUploadAttempt;
    hasCredits: boolean;
  }): Promise<LandingUploadLifecycleResult> => {
    const state = prepareLandingUploadResumeState({
      activeState: activeAttemptRef.current,
      attempt,
      createJobId: () => crypto.randomUUID(),
    });
    activeAttemptRef.current = state;
    setOwnershipPersistence(undefined);

    const result = await executeLandingUploadAttempt({
      state,
      hasCredits,
      dependencies: {
        activateJob: async (createdJobId) => {
          await ofetch(`/api/v1/landing-upload/${createdJobId}/`, {
            method: "PUT",
            body: { status: "UPLOADED" },
          });
        },
        capture: captureLandingEvent,
        createJob: async (createdJobId, transcriptionJobInput) => {
          const response = await ofetch<{ transcriptionJob: { id: string } }>(
            "/api/v1/landing-upload/",
            {
              method: "POST",
              body: { ...transcriptionJobInput, id: createdJobId },
            }
          );
          if (response.transcriptionJob.id !== createdJobId) {
            throw new Error("Landing job response had an unexpected ID");
          }
        },
        getExistingJobIds: getLocalStorageJobIds,
        getJob: async (createdJobId) => {
          const response = await ofetch<{
            data?: GQL_GET_LANDING_JOB_QUERY;
          }>(`/api/v1/landing-upload/${createdJobId}/`, { retry: 0 });
          return response.data?.transcription_jobs_by_pk ?? null;
        },
        isAmbiguousError: isAmbiguousRequestError,
        onAttemptStart: (knownJobId) => {
          setJobId(knownJobId);
          setUploadStatus("LOADING");
          setUploadMessage(t("uploadForm.isUploadingMessage"));
        },
        onJobCreated: setJobId,
        persistJobId: (createdJobId) => setLocalStorageJobId({ jobId: createdJobId }),
        uploadFile,
      },
    });

    if (result.status === "credit_gate" || result.status === "existing_anonymous_job") {
      resetActiveAttempt();
    }

    if (result.status === "existing_anonymous_job") {
      addMessage({
        type: "ERROR",
        text: t("landingUpload.alreadyUploadedMessage"),
      });
      return result;
    }

    if (result.status === "failure") {
      setUploadStatus("ERROR");
      setUploadMessage(t("uploadForm.couldNotUploadMessage"));
      setUploadAlertOpen(true);
      addMessage({
        type: "ERROR",
        text: t("uploadForm.errorUploadingMessage"),
      });
      return result;
    }

    if (result.status === "success") {
      const isFile = attempt.dimensions.input_method === "file";
      activeAttemptRef.current = null;
      setOwnershipPersistence(result.ownershipPersistence);
      setUploadStatus("SUCCESS");
      setUploadMessage(
        isFile
          ? t("uploadForm.videoUploadedMessage")
          : t("uploadForm.videoIsProcessingShortMessage")
      );
      setUploadAlertOpen(true);
      addMessage({
        type: "SUCCESS",
        text: isFile
          ? t("uploadForm.videoUploadedMessage")
          : t("uploadForm.videoIsProcessingMessage"),
      });
    }

    return result;
  };

  return {
    captureLandingEvent,
    handleInsertTranscriptionJob,
    jobId,
    ownershipPersistence,
    resetActiveAttempt,
    setJobId,
    uploadStatus,
    setUploadStatus,
    uploader,
  };
};
